import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { StyleService } from '../../services/style.service';
import { IStyle, StyleStatus } from '../../interfaces/garments/style.interface';
import { IBuyer } from '../../interfaces/garments/buyer.interface';
import { PERMISSIONS } from '../../constants/permissions';
import BuyerAutocomplete from './BuyerAutocomplete';
import { enumLabel } from '../../utils/garmentsLabels';

const SEARCH_DEBOUNCE_MS = 300;

const STATUS_FILTERS: (StyleStatus | 'ALL')[] = ['ALL', 'DEVELOPMENT', 'QUOTED', 'CONFIRMED', 'ARCHIVED'];

const STATUS_COLORS: Record<StyleStatus, 'default' | 'info' | 'primary' | 'success' | 'warning'> = {
    DEVELOPMENT: 'info',
    QUOTED: 'primary',
    CONFIRMED: 'success',
    ARCHIVED: 'default',
};

/** Style master list (garments-merchandising T8) — G20: MUI Table + TablePagination, never
 *  DataGrid, server-side paged through the Page<T> endpoint. Row click navigates to `StyleForm`
 *  (view/edit/cost-sheet/attachments); Archive/Reactivate is offered inline as the
 *  deactivate-not-delete affordance for this referenced master data. */
export function StyleList() {
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const toast = useToast();
    const canManage = hasPermission(PERMISSIONS.STYLE_MANAGE);

    const [rows, setRows] = useState<IStyle[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<StyleStatus | 'ALL'>('ALL');
    const [buyerFilter, setBuyerFilter] = useState<IBuyer | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        try {
            setLoading(true);
            const res = await StyleService.list(
                institutionId, page, size,
                buyerFilter?.id,
                statusFilter === 'ALL' ? undefined : statusFilter,
                search.trim() || undefined
            );
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load styles');
        } finally {
            setLoading(false);
        }
    }, [page, size, search, statusFilter, buyerFilter, toast]);

    useEffect(() => {
        const handle = setTimeout(load, search ? SEARCH_DEBOUNCE_MS : 0);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, size, search, statusFilter, buyerFilter]);

    const handleToggleActive = async (style: IStyle, e: React.MouseEvent) => {
        e.stopPropagation();
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        const verb = style.active ? 'archive' : 'reactivate';
        if (!window.confirm(`Are you sure you want to ${verb} "${style.name}"?`)) return;
        try {
            const updated = style.active
                ? await StyleService.archive(institutionId, style.id)
                : await StyleService.activate(institutionId, style.id);
            setRows(prev => prev.map(s => (s.id === updated.id ? updated : s)));
            toast.showSuccess(style.active ? 'Style archived' : 'Style reactivated');
        } catch (err) {
            toast.showApiError(err, `Failed to ${verb} style`);
        }
    };

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.styles.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5">Styles</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }} data-help-section="merchandising.styles.filter">
                    <TextField
                        data-help="merchandising.styles.search"
                        size="small"
                        label="Search"
                        placeholder="Style number or name..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        sx={{ flex: '1 1 200px', maxWidth: 320 }}
                    />
                    <Box sx={{ flex: '1 1 220px', maxWidth: 340 }}>
                        <BuyerAutocomplete
                            value={buyerFilter}
                            onChange={buyer => { setBuyerFilter(buyer); setPage(0); }}
                            label="Buyer"
                            placeholder="Filter by buyer..."
                            size="small"
                        />
                    </Box>
                    <TextField
                        data-help="merchandising.styles.statusFilter"
                        select
                        size="small"
                        label="Status"
                        value={statusFilter}
                        onChange={e => { setStatusFilter(e.target.value as StyleStatus | 'ALL'); setPage(0); }}
                        sx={{ flex: '1 1 160px', maxWidth: 280 }}
                    >
                        {STATUS_FILTERS.map(s => (
                            <MenuItem key={s} value={s}>{s === 'ALL' ? 'All' : s}</MenuItem>
                        ))}
                    </TextField>
                    {canManage && (
                        <Button data-help="merchandising.styles.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/merchandising/styles/new')}>
                            New Style
                        </Button>
                    )}
                </Box>
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="merchandising.styles.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="merchandising.styles.col.number">Style Number</TableCell>
                                <TableCell data-help="merchandising.styles.col.name">Name</TableCell>
                                <TableCell data-help="merchandising.styles.col.garmentType">Garment Type</TableCell>
                                <TableCell data-help="merchandising.styles.col.season">Season</TableCell>
                                <TableCell data-help="merchandising.styles.col.status">Status</TableCell>
                                <TableCell align="right" data-help="merchandising.styles.totalCost">Total Cost</TableCell>
                                <TableCell data-help="merchandising.styles.col.active">Active</TableCell>
                                {canManage && <TableCell align="right" data-help="merchandising.styles.col.actions">Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => (
                                <TableRow
                                    key={r.id}
                                    hover
                                    sx={{ cursor: 'pointer', opacity: r.active ? 1 : 0.55 }}
                                    onClick={() => navigate(`/merchandising/styles/${r.id}`)}
                                >
                                    <TableCell>{r.styleNumber}</TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell>{r.garmentType ?? '—'}</TableCell>
                                    <TableCell>{r.season ?? '—'}</TableCell>
                                    <TableCell>
                                        <Chip data-help="merchandising.styles.stage" size="small" color={STATUS_COLORS[r.status]} label={enumLabel(r.status)} />
                                    </TableCell>
                                    <TableCell align="right">{r.totalCost.toFixed(2)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={r.active ? 'Active' : 'Inactive'}
                                            color={r.active ? 'success' : 'default'}
                                            size="small"
                                            variant={r.active ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    {canManage && (
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                color={r.active ? 'warning' : 'success'}
                                                data-help="merchandising.styles.archive" onClick={e => handleToggleActive(r, e)}
                                            >
                                                {r.active ? 'Archive' : 'Reactivate'}
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={canManage ? 8 : 7} align="center">No styles found</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={e => { setSize(Number(e.target.value)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </TableContainer>
            )}
        </Box>
    );
}

export default StyleList;
